import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { supabase } from '@/lib/supabase';
import type { FilterGroup, FilterRule, FilterEntity, FilterAttribute, FilterField } from '@/types/filters';
import type { Supplier } from '../types';

type Option = { value: string; label: string };


export const useSupplierFilterBuilder = (suppliers: Supplier[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-suppliers-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-suppliers-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    categories: Option[];
    trades: Option[];
    createdBy: Option[];
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
  }>({ 
    categories: [], 
    trades: [], 
    createdBy: [],
    cities: [], 
    provinces: [], 
    suburbs: [], 
    tags: []
  });

  // Fetch comprehensive filter options from database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch categories, trades, and profiles in parallel
        const [categoriesRes, tradesRes, profilesRes] = await Promise.all([
          supabase.from('supplier_categories').select('id, name'),
          supabase.from('supplier_trades').select('id, name'),
          supabase.from('profiles').select('id, first_name, last_name')
        ]);

        const categoryOptions: Option[] = (categoriesRes.data || []).map(cat => ({
          value: cat.id,
          label: cat.name
        }));

        const tradeOptions: Option[] = (tradesRes.data || []).map(trade => ({
          value: trade.id,
          label: trade.name
        }));

        const createdByOptions: Option[] = (profilesRes.data || []).map(profile => ({
          value: profile.id,
          label: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown'
        }));

        setOptionsOverride(prev => ({
          ...prev,
          categories: categoryOptions,
          trades: tradeOptions,
          createdBy: createdByOptions
        }));
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchOptions();
  }, []);

  // Generate dynamic options from suppliers data
  const dynamicOptions = useMemo(() => {
    const citiesSet = new Set<string>();
    const provincesSet = new Set<string>();
    const suburbsSet = new Set<string>();
    const tagsSet = new Set<string>();

    suppliers.forEach(supplier => {
      if (supplier.city) citiesSet.add(supplier.city);
      if (supplier.province_state) provincesSet.add(supplier.province_state);
      if (supplier.suburb) suburbsSet.add(supplier.suburb);
      
      // Extract tags from metadata
      if (supplier.metadata && Array.isArray(supplier.metadata.tags)) {
        supplier.metadata.tags.forEach((tag: string) => {
          if (tag) tagsSet.add(tag);
        });
      }
    });

    const cities = Array.from(citiesSet).sort().map(city => ({ value: city, label: city }));
    const provinces = Array.from(provincesSet).sort().map(prov => ({ value: prov, label: prov }));
    const suburbs = Array.from(suburbsSet).sort().map(suburb => ({ value: suburb, label: suburb }));
    const tags = Array.from(tagsSet).sort().map(tag => ({ value: tag, label: tag }));

    return {
      categories: optionsOverride.categories.length > 0 ? optionsOverride.categories : [],
      trades: optionsOverride.trades.length > 0 ? optionsOverride.trades : [],
      createdBy: optionsOverride.createdBy.length > 0 ? optionsOverride.createdBy : [],
      cities: [...optionsOverride.cities, ...cities].filter((option, index, self) => 
        index === self.findIndex(o => o.value === option.value)
      ),
      provinces: [...optionsOverride.provinces, ...provinces].filter((option, index, self) => 
        index === self.findIndex(o => o.value === option.value)
      ),
      suburbs: [...optionsOverride.suburbs, ...suburbs].filter((option, index, self) => 
        index === self.findIndex(o => o.value === option.value)
      ),
      tags: [...optionsOverride.tags, ...tags].filter((option, index, self) => 
        index === self.findIndex(o => o.value === option.value)
      )
    };
  }, [suppliers, optionsOverride]);

  // Filter entities
  const filterEntities: FilterEntity[] = [
    { key: 'supplier', label: 'Supplier' },
    { key: 'location', label: 'Location' },
    { key: 'business', label: 'Business' },
    { key: 'meta', label: 'Meta' }
  ];

  // Attributes per entity
  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    supplier: [
      { key: 'name', label: "'s Name", type: 'text' },
      { key: 'contact_name', label: "'s Contact Name", type: 'text' },
      { key: 'email', label: "'s Email", type: 'text' },
      { key: 'phone', label: "'s Phone", type: 'text' },
      { key: 'website', label: "'s Website", type: 'text' },
      { key: 'default_minimum_order', label: "'s Minimum Order", type: 'number' }
    ],
    location: [
      { key: 'street_address', label: "'s Street Address", type: 'text' },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: dynamicOptions.suburbs },
      { key: 'city', label: "'s City", type: 'select', options: dynamicOptions.cities },
      { key: 'province_state', label: "'s Province/State", type: 'select', options: dynamicOptions.provinces },
      { key: 'postal_code', label: "'s Postal Code", type: 'text' }
    ],
    business: [
      { key: 'category', label: "'s Classification", type: 'select', options: dynamicOptions.categories },
      { key: 'trades', label: "'s Trades", type: 'select', options: dynamicOptions.trades },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags }
    ],
    meta: [
      { key: 'created_by', label: "'s Created By", type: 'select', options: dynamicOptions.createdBy },
      { key: 'created_at', label: "'s Created Date", type: 'date' },
      { key: 'updated_at', label: "'s Updated Date", type: 'date' }
    ]
  };

  // Generate filter fields for UI
  const filterFields = useMemo(() => {
    const fields: FilterField[] = [];
    filterEntities.forEach(entity => {
      const attributes = filterAttributesByEntity[entity.key] || [];
      attributes.forEach(attr => {
        fields.push({
          key: `${entity.key}.${attr.key}`,
          label: `${entity.label} - ${attr.label}`,
          type: attr.type,
          options: attr.options
        });
      });
    });
    return fields;
  }, [dynamicOptions]);

  // Filter group management functions
  const addGroup = useCallback(() => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };

    const newGroup: FilterGroup = {
      id: Date.now().toString(),
      rules: [newRule],
      logic: 'AND'
    };

    setDraftGroups(prev => [...prev, newGroup]);
  }, []);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      entity: '',
      attribute: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };

    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: [...group.rules, newRule]
        };
      }
      return group;
    }));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: group.rules.map(rule => 
            rule.id === ruleId ? { ...rule, ...updates } : rule
          )
        };
      }
      return group;
    }));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: group.rules.filter(rule => rule.id !== ruleId)
        };
      }
      return group;
    }));
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedGroups([...draftGroups]);
  }, [draftGroups]);

  // Remove individual applied rule
  const removeAppliedRule = useCallback((groupId: string, ruleId: string) => {
    setAppliedGroups(currentGroups => {
      return currentGroups.map(group => {
        if (group.id === groupId) {
          const updatedRules = group.rules.filter(rule => rule.id !== ruleId);
          if (updatedRules.length === 0) {
            return null; // Mark group for removal
          }
          return { ...group, rules: updatedRules };
        }
        return group;
      }).filter(Boolean) as FilterGroup[];
    });
  }, []);

  // Remove entire applied group
  const removeAppliedGroup = useCallback((groupId: string) => {
    setAppliedGroups(currentGroups => 
      currentGroups.filter(group => group.id !== groupId)
    );
  }, []);

  const isRuleComplete = useCallback((rule: FilterRule): boolean => {
    return !!(rule.entity && rule.attribute && rule.operator && 
      (rule.operator === 'is_empty' || rule.operator === 'is_not_empty' || rule.value !== ''));
  }, []);

  // Helper function to get field value from supplier
  const getFieldValue = useCallback((supplier: Supplier, rule: FilterRule): any => {
    const [entity, attribute] = rule.entity.includes('.') 
      ? rule.entity.split('.') 
      : [rule.entity, rule.attribute];

    switch (entity) {
      case 'supplier':
        switch (attribute) {
          case 'name': return supplier.name;
          case 'contact_name': return supplier.contact_name;
          case 'email': return supplier.email;
          case 'phone': return supplier.phone;
          case 'website': return supplier.website;
          case 'default_minimum_order': return supplier.default_minimum_order;
          default: return null;
        }
      case 'location':
        switch (attribute) {
          case 'street_address': return supplier.street_address;
          case 'suburb': return supplier.suburb;
          case 'city': return supplier.city;
          case 'province_state': return supplier.province_state;
          case 'postal_code': return supplier.postal_code;
          default: return null;
        }
      case 'business':
        switch (attribute) {
          case 'category': return supplier.category_id;
          case 'trades': return supplier.trade_ids;
          case 'tags': return supplier.metadata?.tags || [];
          default: return null;
        }
      case 'meta':
        switch (attribute) {
          case 'created_by': return supplier.created_by;
          case 'created_at': return supplier.created_at;
          case 'updated_at': return supplier.updated_at;
          default: return null;
        }
      default:
        return null;
    }
  }, []);

  // Helper function to evaluate a single rule
  const evaluateRule = useCallback((supplier: Supplier, rule: FilterRule): boolean => {
    if (!isRuleComplete(rule)) return true;

    const fieldValue = getFieldValue(supplier, rule);
    const { operator, value } = rule;

    switch (operator) {
      case 'equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => value.includes(v));
          }
          return value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(value);
        }
        return fieldValue === value;
      case 'not_equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => value.includes(v));
          }
          return !value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return !fieldValue.includes(value);
        }
        return fieldValue !== value;
      case 'contains':
        return fieldValue && fieldValue.toString().toLowerCase().includes(value.toLowerCase());
      case 'not_contains':
        return !fieldValue || !fieldValue.toString().toLowerCase().includes(value.toLowerCase());
      case 'greater_than':
        return Number(fieldValue) > Number(value);
      case 'less_than':
        return Number(fieldValue) < Number(value);
      case 'between':
        if (Array.isArray(value) && value.length === 2) {
          const numValue = Number(fieldValue);
          return numValue >= Number(value[0]) && numValue <= Number(value[1]);
        }
        return false;
      case 'in':
        if (Array.isArray(fieldValue) && Array.isArray(value)) {
          // For array fields (like trade_ids), check if any field value matches any filter value
          return fieldValue.some(item => value.includes(item));
        }
        return Array.isArray(value) ? value.includes(fieldValue) : false;
      case 'not_in':
        if (Array.isArray(fieldValue) && Array.isArray(value)) {
          // For array fields (like trade_ids), check that no field values match any filter values
          return !fieldValue.some(item => value.includes(item));
        }
        return Array.isArray(value) ? !value.includes(fieldValue) : true;
      case 'is_empty':
        return !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
      case 'is_not_empty':
        return !!fieldValue && fieldValue !== '' && !(Array.isArray(fieldValue) && fieldValue.length === 0);
      default:
        return true;
    }
  }, [getFieldValue, isRuleComplete]);

  // Apply filters to suppliers
  const filteredSuppliers = useMemo(() => {
    if (appliedGroups.length === 0) return suppliers;

    return suppliers.filter(supplier => {
      return appliedGroups.every(group => {
        if (group.rules.length === 0) return true;

        const ruleResults = group.rules.map(rule => evaluateRule(supplier, rule));
        
        return group.logic === 'OR' 
          ? ruleResults.some(result => result)
          : ruleResults.every(result => result);
      });
    });
  }, [suppliers, appliedGroups, evaluateRule]);

  const hasActiveFilters = appliedGroups.length > 0 && appliedGroups.some(group => group.rules.some(rule => isRuleComplete(rule)));
  const hasUnappliedChanges = JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  const canApplyFilters = draftGroups.length > 0 && draftGroups.some(group => group.rules.some(rule => isRuleComplete(rule)));

  return {
    // Filter state
    draftGroups,
    appliedGroups,
    filteredSuppliers,

    // Filter management functions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    removeAppliedRule,
    removeAppliedGroup,

    // Filter metadata
    filterFields,
    filterEntities,
    filterAttributesByEntity,

    // Helper states
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
  };
};